import { nanoid } from 'nanoid';
import { getSupabaseClient } from '@/lib/supabaseClient';
import { runtimeMode } from '@/lib/runtimeMode';
import { storage } from '@/lib/storage';
import { namesAreSimilar, normalizePhone, normalizePlate } from '@/lib/customerIdentity';
import {
  CustomerIntakeRequest,
  CustomerIntakeResolutionAction,
  CustomerIntakeResolutionResult,
  VehicleRelationship,
} from '@/types';
import type { Database } from '@/types/database';
import { customerService } from '@/services/customerService';
import { vehicleService } from '@/services/vehicleService';

const INTAKE_KEY = 'customer_intake_requests';

type IntakeRow = Database['public']['Tables']['customer_intake_requests']['Row'];

export type CreateCustomerIntakeInput = {
  shop_id?: string;
  full_name: string;
  phone: string;
  car_make_model: string;
  plate_number: string;
  service_needed: string;
  vehicle_relationship?: VehicleRelationship;
};

export type ResolveCustomerIntakeInput = {
  action: CustomerIntakeResolutionAction;
  customer_id?: string;
  vehicle_id?: string;
  duplicate_score?: number;
  allow_unsafe?: boolean;
};

const mapRow = (row: IntakeRow): CustomerIntakeRequest => ({
  id: row.id,
  shop_id: row.shop_id,
  full_name: row.full_name,
  phone: row.phone,
  normalized_phone: row.normalized_phone,
  car_make_model: row.car_make_model,
  plate_number: row.plate_number,
  normalized_plate: row.normalized_plate,
  service_needed: row.service_needed,
  vehicle_relationship: row.vehicle_relationship ?? undefined,
  status: row.status,
  matched_customer_id: row.matched_customer_id ?? undefined,
  matched_vehicle_id: row.matched_vehicle_id ?? undefined,
  duplicate_score: row.duplicate_score ?? undefined,
  created_at: row.created_at,
  reviewed_at: row.reviewed_at ?? undefined,
  reviewed_by: row.reviewed_by ?? undefined,
});

const localGetAll = () => storage.get<CustomerIntakeRequest[]>(INTAKE_KEY) ?? [];

const updateLocalRequest = (
  requestId: string,
  result: CustomerIntakeResolutionResult,
  duplicateScore?: number,
) => {
  const requests = localGetAll();
  const index = requests.findIndex((request) => request.id === requestId);
  if (index === -1) throw new Error('QR entry was not found.');

  requests[index] = {
    ...requests[index],
    status: result.status,
    matched_customer_id: result.customer_id,
    matched_vehicle_id: result.vehicle_id,
    duplicate_score: duplicateScore,
    reviewed_at: new Date().toISOString(),
  };
  storage.set(INTAKE_KEY, requests);
};

const parseResolutionResult = (data: unknown): CustomerIntakeResolutionResult => {
  if (!data || typeof data !== 'object' || !('status' in data)) {
    throw new Error('The QR entry was reviewed, but the result could not be read.');
  }

  const result = data as Record<string, unknown>;
  return {
    customer_id: typeof result.customer_id === 'string' ? result.customer_id : undefined,
    vehicle_id: typeof result.vehicle_id === 'string' ? result.vehicle_id : undefined,
    status: result.status as CustomerIntakeResolutionResult['status'],
  };
};

export const customerIntakeService = {
  submit: async (input: CreateCustomerIntakeInput): Promise<void> => {
    if (!input.shop_id?.trim()) throw new Error('This registration link is missing the shop ID.');
    if (!input.full_name.trim() || !input.phone.trim() || !input.plate_number.trim() || !input.car_make_model.trim()) {
      throw new Error('Please complete the required registration fields.');
    }
    if (!input.service_needed.trim()) throw new Error('Please choose the service needed today.');

    if (runtimeMode.isSupabaseConfigured) {
      const { error } = await getSupabaseClient().from('customer_intake_requests').insert({
        shop_id: input.shop_id,
        full_name: input.full_name.trim(),
        phone: input.phone.trim(),
        car_make_model: input.car_make_model.trim(),
        plate_number: input.plate_number.trim(),
        service_needed: input.service_needed,
        vehicle_relationship: input.vehicle_relationship ?? null,
      });

      if (error) throw new Error(error.message);
      return;
    }

    const requests = localGetAll();
    const normalizedPhone = normalizePhone(input.phone);
    const normalizedPlate = normalizePlate(input.plate_number);
    const recentDuplicate = requests.some(
      (request) =>
        request.shop_id === input.shop_id &&
        request.normalized_phone === normalizedPhone &&
        request.normalized_plate === normalizedPlate &&
        Date.now() - new Date(request.created_at).getTime() < 30_000,
    );
    if (recentDuplicate) throw new Error('Please wait before submitting this registration again.');

    requests.unshift({
      id: nanoid(),
      shop_id: input.shop_id,
      full_name: input.full_name.trim(),
      phone: input.phone.trim(),
      normalized_phone: normalizedPhone,
      car_make_model: input.car_make_model.trim(),
      plate_number: input.plate_number.trim().toUpperCase(),
      normalized_plate: normalizedPlate,
      service_needed: input.service_needed,
      vehicle_relationship: input.vehicle_relationship,
      status: 'pending',
      created_at: new Date().toISOString(),
    });
    storage.set(INTAKE_KEY, requests);
  },

  getPending: async (shopId?: string | null): Promise<CustomerIntakeRequest[]> => {
    if (runtimeMode.isSupabaseConfigured) {
      if (!shopId) return [];
      const { data, error } = await getSupabaseClient()
        .from('customer_intake_requests')
        .select('*')
        .eq('shop_id', shopId)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw new Error(error.message);
      return (data ?? []).map(mapRow);
    }

    return localGetAll().filter((request) => request.status === 'pending');
  },

  resolve: async (
    id: string,
    input: ResolveCustomerIntakeInput,
    shopId?: string | null,
  ): Promise<CustomerIntakeResolutionResult> => {
    if (runtimeMode.isSupabaseConfigured) {
      if (!shopId) throw new Error('A shop profile is required to review QR entries.');
      const { data, error } = await getSupabaseClient().rpc('resolve_customer_intake_request', {
        intake_request_id: id,
        resolution_action: input.action,
        target_customer_id: input.customer_id ?? null,
        target_vehicle_id: input.vehicle_id ?? null,
        duplicate_score_value: input.duplicate_score ?? null,
        allow_unsafe: input.allow_unsafe ?? false,
      });
      if (error) throw new Error(error.message);
      return parseResolutionResult(data);
    }

    const requests = localGetAll();
    const request = requests.find((candidate) => candidate.id === id);
    if (!request) throw new Error('QR entry was not found.');
    if (request.status !== 'pending') throw new Error('This QR entry was already reviewed.');

    if (input.action === 'ignore_intake') {
      const result: CustomerIntakeResolutionResult = { status: 'ignored' };
      updateLocalRequest(id, result, input.duplicate_score);
      return result;
    }

    const plateMatch = vehicleService
      .getAll()
      .find((vehicle) => normalizePlate(vehicle.plate_number) === request.normalized_plate);
    const phoneMatch = customerService
      .getAll()
      .find((customer) => normalizePhone(customer.phone) === request.normalized_phone);

    let createdCustomerId: string | undefined;
    let createdVehicleId: string | undefined;

    try {
      if (input.action === 'create_customer_and_vehicle') {
        if (plateMatch) throw new Error('This plate already exists. Link the QR entry to the existing car.');
        if (phoneMatch && !input.allow_unsafe) {
          throw new Error('A customer with this phone already exists. Confirm before creating a separate customer.');
        }

        const customer = customerService.create({ name: request.full_name, phone: request.phone });
        createdCustomerId = customer.id;
        const vehicle = vehicleService.create({
          customer_id: customer.id,
          customer_name: customer.name,
          plate_number: request.plate_number,
          make: request.car_make_model,
          relationship: request.vehicle_relationship,
        });
        createdVehicleId = vehicle.id;
      } else if (input.action === 'link_existing_customer_add_vehicle') {
        const customer = input.customer_id ? customerService.getById(input.customer_id) : null;
        if (!customer) throw new Error('Choose an existing customer before adding the car.');
        if (plateMatch) throw new Error('This plate already exists. Link the QR entry to the existing car.');
        if (
          (
            normalizePhone(customer.phone) !== request.normalized_phone ||
            !namesAreSimilar(customer.name, request.full_name)
          ) &&
          !input.allow_unsafe
        ) {
          throw new Error('The selected customer details differ. Confirm before adding this car.');
        }

        const vehicle = vehicleService.create({
          customer_id: customer.id,
          customer_name: customer.name,
          plate_number: request.plate_number,
          make: request.car_make_model,
          relationship: request.vehicle_relationship,
        });
        createdVehicleId = vehicle.id;
        createdCustomerId = customer.id;
      } else {
        const customer = input.customer_id ? customerService.getById(input.customer_id) : null;
        const vehicle = input.vehicle_id ? vehicleService.getById(input.vehicle_id) : null;
        if (!customer || !vehicle) throw new Error('Choose the existing customer and car before linking.');
        if (vehicle.customer_id !== customer.id) throw new Error('The selected car does not belong to the selected customer.');
        if (normalizePlate(vehicle.plate_number) !== request.normalized_plate) {
          throw new Error('The selected car has a different plate and cannot be linked to this QR entry.');
        }
        if (
          (
            normalizePhone(customer.phone) !== request.normalized_phone ||
            !namesAreSimilar(customer.name, request.full_name)
          ) &&
          !input.allow_unsafe
        ) {
          throw new Error('The customer details differ. Confirm before linking.');
        }
        createdCustomerId = customer.id;
        createdVehicleId = vehicle.id;
      }

      const result: CustomerIntakeResolutionResult = {
        customer_id: createdCustomerId,
        vehicle_id: createdVehicleId,
        status: input.action === 'create_customer_and_vehicle' ? 'created' : 'linked',
      };
      updateLocalRequest(id, result, input.duplicate_score);
      return result;
    } catch (error) {
      if (createdVehicleId) vehicleService.delete(createdVehicleId);
      if (input.action === 'create_customer_and_vehicle' && createdCustomerId) {
        customerService.delete(createdCustomerId);
      }
      throw error;
    }
  },
};
